/**
 * The map playground (`?play=1` on a map URL, dev builds): the real app with you and friends placed by hand, so how the
 * map looks while people stand and walk can be checked in the browser before a phone build.
 *
 * Kept simple on purpose (Sam, 2026-10-19: the first version - GPS noise, wandering bots, a joystick - was chaos): you
 * walk with the keyboard (relative to the screen) and turn your compass; friends stand where you put them and move only when you
 * drag them. Your phone-style fix lands once a second, exact, so the map's own smoothing is what you see.
 *
 * It is a live map scenario (mapScenarios.js): the same { self, headingDeg, peers, gpsPeers } object the Maps page reads.
 */
import { destinationPoint, haversineMeters } from '../proximity/engine/geodesy';
import { formatFindDistance } from '../proximity/engine/findDistanceFormat';
import { formatStalenessShort } from '../proximity/engine/staleness';

const NAMES = [
  'Olivia',
  'Jo',
  'Maya',
  'Leo',
  'Priya',
  'Ava',
  'Noah',
  'Zoe',
  'Kai',
  'Mia',
  'Eli',
  'Sam',
];
const TURN_DPS = 90;
const FIX_MS = 1000; // a phone reports about once a second
const HEARTBEAT_MS = 20_000; // a standing friend re-publishes now and then, so they never read as stale

export function playRequested(search) {
  return Boolean(
    import.meta.env.DEV && typeof search === 'string' && new URLSearchParams(search).get('play'),
  );
}

const bearingTo = (a, b) => {
  const φ1 = (a.lat * Math.PI) / 180,
    φ2 = (b.lat * Math.PI) / 180,
    Δλ = ((b.lng - a.lng) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2),
    x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/** @param bounds [[minLng,minLat],[maxLng,maxLat]] - the venue box */
export function createPlayground({ bounds }) {
  const centre = { lat: (bounds[0][1] + bounds[1][1]) / 2, lng: (bounds[0][0] + bounds[1][0]) / 2 };
  // speed: your walking pace (Shift doubles it). Accuracy circles for you and for friends are set separately.
  const settings = { speedMps: 1.4, selfAccuracyM: 12, friendAccuracyM: 12 };
  let viewBearing = () => null; // the map's "up the screen" compass bearing (scene.viewBearing), handed in by the panel
  const truth = { lat: centre.lat, lng: centre.lng, heading: 0, speed: 0 };
  const keys = new Set();
  const listeners = new Set();
  const friends = [];
  let nextId = 0;
  let version = 0;

  const sim = {
    name: 'play',
    self: {
      lat: centre.lat,
      lng: centre.lng,
      accuracyMeters: settings.selfAccuracyM,
      headingDegrees: 0,
      timestampMs: Date.now(),
    },
    headingDeg: 0,
    peers: [],
    gpsPeers: [],
    trackedPeerId: null,
    selectPeerId: null,
    guide: false,
  };

  const emit = () => {
    version += 1;
    for (const l of listeners) l();
  };
  const inBox = (p) =>
    p.lng > bounds[0][0] && p.lng < bounds[1][0] && p.lat > bounds[0][1] && p.lat < bounds[1][1];

  function rowFor(f, now) {
    const m = haversineMeters(sim.self.lat, sim.self.lng, f.lat, f.lng);
    return {
      peerId: f.id,
      displayName: f.name,
      photoUrl: null,
      display: {
        kind: 'gps-live',
        listPreview: `${formatFindDistance(m)} · ${formatStalenessShort(f.at, now)}`,
        distanceMeters: m,
        sortTier: 1,
        sortDistance: m,
        gpsLastKnownAt: f.at,
      },
      peer: {
        peerId: f.id,
        rssi: -90,
        smoothedRssi: -90,
        estimatedDistance: m,
        zone: 'far',
        lastSeenAt: now - 60_000,
        gpsDistance: m,
        gpsBearingDeg: bearingTo(sim.self, f),
        gpsSource: 'remote',
        uwbActive: false,
      },
      bearingDeg: bearingTo(sim.self, f),
      uwbActive: false,
      uwbDistance: null,
      uwbDirectionAvailable: false,
    };
  }
  /** Rebuild what the map reads from the friends list. */
  function publish(now = Date.now()) {
    sim.gpsPeers = friends.map((f) => ({
      id: f.id,
      source: 'remote',
      lat: f.lat,
      lng: f.lng,
      accuracyMeters: settings.friendAccuracyM,
      headingDegrees: null,
      lastKnownAt: f.at,
    }));
    sim.peers = friends.map((f) => rowFor(f, now));
    emit();
  }

  function addFriend(ll) {
    const i = nextId++;
    const f = {
      id: `play-${i}`,
      name: NAMES[i % NAMES.length],
      lat: ll.lat,
      lng: ll.lng,
      at: Date.now(),
    };
    friends.push(f);
    publish();
    return f;
  }
  function moveFriend(id, ll) {
    const f = friends.find((x) => x.id === id);
    if (!f) return;
    f.lat = ll.lat;
    f.lng = ll.lng;
    f.at = Date.now();
    publish();
  }
  function removeFriend(id) {
    const i = friends.findIndex((x) => x.id === id);
    if (i >= 0) {
      friends.splice(i, 1);
      publish();
    }
  }
  function clearFriends() {
    friends.length = 0;
    publish();
  }
  /** Add or remove friends until there are n; new ones stand 15-80 m from you, inside the venue. */
  function setFriendCount(n) {
    n = Math.max(0, Math.min(30, Math.round(n)));
    while (friends.length > n) friends.pop();
    let tries = 0;
    while (friends.length < n && tries++ < 200) {
      const p = destinationPoint(
        truth.lat,
        truth.lng,
        15 + Math.random() * 65,
        Math.random() * 360,
      );
      if (inBox(p)) {
        const i = nextId++;
        friends.push({
          id: `play-${i}`,
          name: NAMES[i % NAMES.length],
          lat: p.lat,
          lng: p.lng,
          at: Date.now(),
        });
      }
    }
    publish();
  }

  function takeFix(now) {
    sim.self = {
      lat: truth.lat,
      lng: truth.lng,
      accuracyMeters: settings.selfAccuracyM,
      headingDegrees: truth.heading,
      timestampMs: now,
    };
  }

  let raf = 0,
    last = 0,
    lastFix = 0,
    lastBeat = 0;
  function frame(t) {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.1, (t - (last || t)) / 1000);
    last = t;
    const now = Date.now();
    // Keys by physical position (e.code), so Shift or a keyboard layout never changes what they do.
    let turn = 0,
      fwd = 0,
      side = 0;
    if (keys.has('KeyQ')) turn -= 1;
    if (keys.has('KeyE')) turn += 1;
    if (keys.has('KeyW') || keys.has('ArrowUp')) fwd += 1;
    if (keys.has('KeyS') || keys.has('ArrowDown')) fwd -= 1;
    if (keys.has('KeyA') || keys.has('ArrowLeft')) side -= 1;
    if (keys.has('KeyD') || keys.has('ArrowRight')) side += 1;
    if (turn) {
      truth.heading = (truth.heading + turn * TURN_DPS * dt + 360) % 360;
      const h = Math.round(truth.heading);
      if (h !== sim.headingDeg) {
        sim.headingDeg = h;
        emit();
      }
    }
    const jog = keys.has('ShiftLeft') || keys.has('ShiftRight');
    truth.speed = fwd || side ? settings.speedMps * (jog ? 2 : 1) : 0;
    if (truth.speed) {
      // Relative to the SCREEN (Sam, 2026-10-19): W is up the screen however the map is turned, nav view included.
      const up = viewBearing() ?? truth.heading;
      const n = destinationPoint(
        truth.lat,
        truth.lng,
        truth.speed * dt,
        (up + (Math.atan2(side, fwd) * 180) / Math.PI + 360) % 360,
      );
      if (inBox(n)) {
        truth.lat = n.lat;
        truth.lng = n.lng;
      }
    }
    if (now - lastFix >= FIX_MS) {
      const moved = sim.self.lat !== truth.lat || sim.self.lng !== truth.lng;
      lastFix = now;
      takeFix(now);
      if (moved && friends.length) publish(now); // distances in the list follow you
    }
    if (now - lastBeat >= HEARTBEAT_MS) {
      lastBeat = now;
      for (const f of friends) f.at = now;
      if (friends.length) publish(now);
    }
  }

  const isTyping = (e) =>
    /^(input|textarea|select)$/i.test(e.target?.tagName || '') || e.target?.isContentEditable;
  const MOVE_KEYS = [
    'KeyW',
    'KeyA',
    'KeyS',
    'KeyD',
    'KeyQ',
    'KeyE',
    'ShiftLeft',
    'ShiftRight',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
  ];
  const onDown = (e) => {
    if (isTyping(e) || e.metaKey || e.ctrlKey) return;
    if (MOVE_KEYS.includes(e.code)) {
      keys.add(e.code);
      if (e.code.startsWith('Arrow')) e.preventDefault();
    }
  };
  const onUp = (e) => keys.delete(e.code);
  const onBlur = () => keys.clear();

  function start() {
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    takeFix(Date.now());
    // Two friends standing a short walk away, so the map is not empty; right-click adds more.
    if (!friends.length && nextId === 0) {
      addFriend(destinationPoint(centre.lat, centre.lng, 25, 40));
      addFriend(destinationPoint(centre.lat, centre.lng, 60, 250));
    }
    cancelAnimationFrame(raf);
    last = 0;
    raf = requestAnimationFrame(frame);
  }
  function stop() {
    cancelAnimationFrame(raf);
    window.removeEventListener('keydown', onDown);
    window.removeEventListener('keyup', onUp);
    window.removeEventListener('blur', onBlur);
  }

  return {
    sim,
    settings,
    truth,
    friends,
    start,
    stop,
    addFriend,
    moveFriend,
    removeFriend,
    clearFriends,
    setFriendCount,
    /** Put yourself somewhere (QA scripts, the venue-seat checks). */
    placeSelf(ll) {
      truth.lat = ll.lat;
      truth.lng = ll.lng;
      takeFix(Date.now());
      publish();
    },
    setHeading(deg) {
      truth.heading = ((deg % 360) + 360) % 360;
      sim.headingDeg = Math.round(truth.heading);
      emit();
    },
    setSpeed(mps) {
      settings.speedMps = mps;
      emit();
    },
    setSelfAccuracy(m) {
      settings.selfAccuracyM = m;
      takeFix(Date.now());
      emit();
    },
    setFriendAccuracy(m) {
      settings.friendAccuracyM = m;
      publish();
    },
    setViewBearing(fn) {
      viewBearing = typeof fn === 'function' ? fn : () => null;
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    get version() {
      return version;
    },
  };
}
